import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  CreditCard,
  Save,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Upload,
  Trash2,
  Image as ImageIcon,
  Briefcase,
  Receipt
} from "lucide-react";
import api from "@/api";
import { formatPhoneOnInput, capitalizeFirst } from "@/utils/helpers";
import EmailTemplatesManager from "@/components/settings/EmailTemplatesManager";

/**
 * Studio Settings Component
 * Manages company/studio profile data including dual branding logos
 * 
 * STABILITY FIX: Uses isMountedRef to prevent state updates after unmount
 * and clears timeout on unmount to prevent memory leaks
 */
export default function ImpostazioniStudio({ onSettingsChange }) {
  // Ref to track if component is mounted (prevents setState after unmount)
  const isMountedRef = useRef(true);
  const saveTimeoutRef = useRef(null);
  
  const [settings, setSettings] = useState({
    studioName: "",
    subtitle: "",
    address: "",
    city: "",
    province: "",
    postalCode: "",
    vatNumber: "",
    fiscalCode: "",
    reaNumber: "",
    email: "",
    phone: "",
    pec: "",
    iban: "",
    bankName: "",
    logoUrl: null,
    // Dual branding
    studioNameAgenzia: "",
    subtitleAgenzia: "",
    logoStudio: null,
    logoAgenzia: null,
    // Email template
    invoiceEmailSubject: "Fattura n. {invoiceNumber} del {invoiceDate}",
    invoiceEmailBody: "Gentile {clientName},\n\nIn allegato la fattura n. {invoiceNumber} del {invoiceDate} per un importo di {totale} EUR.\n\nCordiali saluti,\n{studioName}",
    // Default ufficio per il dropdown "Metodo Pagamento" in fattura.
    // Modificabile dall'amministratore; "Bonifico" = bonifico bancario
    // vista fattura. Vuoto = fallback "Bonifico" nel form fattura.
    defaultInvoicePaymentMethod: "Bonifico",
    // Default IVA % per nuova fattura (manuale o da pratica).
    defaultInvoiceVatRate: 22.0,
    // Default "Tipo Gestione" per nuova fattura MANUALE
    // ("studio" | "agenzia"). Per fatture create da pratica viene
    // sovrascritto dal tipo pratica (business rule).
    defaultInvoiceManagementType: "agenzia",
    // Default "Contributo Integrativo 4%" attivo nelle nuove fatture.
    defaultInvoiceContributoEnabled: true,
    // Default modalità importo per fatture manuali:
    // true = Lordo (IVA inclusa, scorporo automatico), false = Netto.
    defaultInvoiceImportoLordo: true
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null
  const [hasChanges, setHasChanges] = useState(false);
  
  // Logo state
  const [logoStudioUrl, setLogoStudioUrl] = useState(null);
  const [logoAgenziaUrl, setLogoAgenziaUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(null); // 'studio' | 'agenzia' | null
  const [logoError, setLogoError] = useState(null); // { type, message } | null
  const fileInputStudioRef = useRef(null);
  const fileInputAgenziaRef = useRef(null);
  // Refs holding the LATEST blob URLs, used to safely revoke on unmount only.
  // Using refs avoids triggering cleanup on every state change which would
  // revoke the OTHER logo's still-in-use blob and crash React 19 reconciliation.
  const logoStudioUrlRef = useRef(null);
  const logoAgenziaUrlRef = useRef(null);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const data = await api.getSettings();
      setSettings(data);
      setHasChanges(false);
      
      // Load logo URLs
      if (data.logoStudio) {
        const studioUrl = await api.getLogoUrl('studio');
        if (studioUrl) setLogoUrlSafely('studio', studioUrl);
      }
      if (data.logoAgenzia) {
        const agenziaUrl = await api.getLogoUrl('agenzia');
        if (agenziaUrl) setLogoUrlSafely('agenzia', agenziaUrl);
      }
    } catch (error) {
      console.error("Error loading settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Track mount status - MUST be separate from other effects
  // This ensures isMountedRef stays true as long as component is mounted
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []); // Empty deps - only runs on mount/unmount

  // Cleanup blob URLs and timers on unmount ONLY (no deps to avoid
  // cross-revoking the other logo's still-in-use blob URL during a re-render,
  // which causes React 19 `insertBefore` reconciliation crashes).
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      if (logoStudioUrlRef.current) {
        URL.revokeObjectURL(logoStudioUrlRef.current);
        logoStudioUrlRef.current = null;
      }
      if (logoAgenziaUrlRef.current) {
        URL.revokeObjectURL(logoAgenziaUrlRef.current);
        logoAgenziaUrlRef.current = null;
      }
    };
  }, []);

  // Helper: set a logo URL, revoking the PREVIOUS one for the same logo
  // AFTER React has committed the new src to the DOM (next tick).
  const setLogoUrlSafely = (logoType, newUrl) => {
    const ref = logoType === 'studio' ? logoStudioUrlRef : logoAgenziaUrlRef;
    const setter = logoType === 'studio' ? setLogoStudioUrl : setLogoAgenziaUrl;
    const prev = ref.current;
    ref.current = newUrl;
    setter(newUrl);
    if (prev && prev !== newUrl) {
      // Defer revoke so React reconciliation finishes first
      setTimeout(() => URL.revokeObjectURL(prev), 0);
    }
  };

  const handleChange = (field, value) => {
    let processedValue = value;
    
    // Auto-capitalize text fields
    const textFields = ["studioName", "subtitle", "address", "city", "bankName"];
    if (textFields.includes(field) && value) {
      processedValue = capitalizeFirst(value);
    }
    
    // Format phone numbers
    if ((field === "phone" || field === "agenziaPhone") && value) {
      processedValue = formatPhoneOnInput(value);
    }
    
    setSettings(prev => ({ ...prev, [field]: processedValue }));
    setHasChanges(true);
    setSaveStatus(null);
  };

  const handleLogoUpload = async (logoType, file) => {
    setLogoError(null);
    if (!file) return;

    // Validate file type — only PNG/JPG/JPEG accepted
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    const fileName = (file.name || '').toLowerCase();
    const hasValidExt = /\.(png|jpe?g)$/i.test(fileName);
    if (!allowedTypes.includes(file.type) || !hasValidExt) {
      setLogoError({ type: logoType, message: 'Formato non supportato. Usa solo PNG o JPG.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setLogoError({ type: logoType, message: 'Il file non può superare 5 MB.' });
      return;
    }

    try {
      setUploadingLogo(logoType);
      await api.uploadLogo(logoType, file);

      // Fetch the new blob URL
      const newUrl = await api.getLogoUrl(logoType);
      if (!newUrl) {
        throw new Error('Impossibile recuperare l\'immagine caricata');
      }
      // Use safe setter: it swaps ref+state then defers revoke of OLD url
      setLogoUrlSafely(logoType, newUrl);
      setSettings(prev => ({
        ...prev,
        [logoType === 'studio' ? 'logoStudio' : 'logoAgenzia']: 'uploaded',
      }));
    } catch (error) {
      setLogoError({
        type: logoType,
        message: error?.message || 'Errore durante il caricamento del logo.',
      });
    } finally {
      setUploadingLogo(null);
      // Reset input value so re-selecting the same file still triggers onChange
      const inputRef = logoType === 'studio' ? fileInputStudioRef : fileInputAgenziaRef;
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleLogoDelete = async (logoType) => {
    setLogoError(null);
    if (!window.confirm(`Rimuovere il logo ${logoType === 'studio' ? 'Studio' : 'Agenzia'}?`)) return;

    try {
      await api.deleteLogo(logoType);
      // Safe revoke: store null via safe setter (defers revoke of previous)
      setLogoUrlSafely(logoType, null);
      setSettings(prev => ({
        ...prev,
        [logoType === 'studio' ? 'logoStudio' : 'logoAgenzia']: null,
      }));
    } catch (error) {
      setLogoError({
        type: logoType,
        message: error?.message || 'Errore durante la rimozione del logo.',
      });
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveStatus(null);
      await api.updateSettings(settings);
      
      // Only update state if component is still mounted
      if (!isMountedRef.current) return;
      
      setSaveStatus('success');
      setHasChanges(false);
      
      // Notify parent of settings change
      if (onSettingsChange) {
        onSettingsChange(settings);
      }
      
      // Clear success message after 3 seconds, with cleanup
      // STABILITY FIX: Store timeout ref and check mounted status
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          setSaveStatus(null);
        }
        saveTimeoutRef.current = null;
      }, 3000);
    } catch (error) {
      console.error("Error saving settings:", error);
      if (isMountedRef.current) {
        setSaveStatus('error');
      }
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  };

  if (isLoading) {
    return (
      <Card className="shadow-md">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-3 text-slate-600">Caricamento impostazioni...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            Impostazioni Studio
          </CardTitle>
          <CardDescription>
            Configura i dati dello studio che verranno utilizzati nelle fatture PDF e nella modulistica
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Company Identity */}
      <Card className="shadow-md">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Identità Studio
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="studioName">Nome Studio / Ragione Sociale *</Label>
              <Input
                id="studioName"
                value={settings.studioName}
                onChange={(e) => handleChange("studioName", e.target.value)}
                placeholder="Es. Studio Rossi STP"
                data-testid="settings-studio-name"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="subtitle">Sottotitolo / Attività</Label>
              <Input
                id="subtitle"
                value={settings.subtitle}
                onChange={(e) => handleChange("subtitle", e.target.value)}
                placeholder="Es. Consulenza del Lavoro"
                data-testid="settings-subtitle"
              />
            </div>
            <div>
              <Label htmlFor="vatNumber">Partita IVA *</Label>
              <Input
                id="vatNumber"
                value={settings.vatNumber}
                onChange={(e) => handleChange("vatNumber", e.target.value)}
                placeholder="Es. IT12345678901"
                data-testid="settings-vat"
              />
            </div>
            <div>
              <Label htmlFor="fiscalCode">Codice Fiscale</Label>
              <Input
                id="fiscalCode"
                value={settings.fiscalCode}
                onChange={(e) => handleChange("fiscalCode", e.target.value)}
                placeholder="Es. 12345678901"
                data-testid="settings-fiscal-code"
              />
            </div>
          </div>

          {/* Iscrizione REA — registro Camera di Commercio.
              Deliberatamente separata dal blocco P.IVA/CF perché è
              un identificativo di registro camerale, non un codice
              fiscale/tributario. Vedi /app/memory/PRD.md sezione
              "REA identity field" (19/02/2026). */}
          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-end gap-3">
              <div className="flex-1 max-w-xs">
                <Label htmlFor="reaNumber" className="flex items-center gap-1.5 text-sm">
                  <Building2 className="h-3.5 w-3.5 text-slate-500" />
                  Iscrizione REA n.
                </Label>
                <Input
                  id="reaNumber"
                  value={settings.reaNumber || ""}
                  onChange={(e) => handleChange("reaNumber", e.target.value)}
                  placeholder="Es. RM-1626139"
                  data-testid="settings-rea-number"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Repertorio Economico Amministrativo — Camera di Commercio.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dual Branding - Logos */}
      <Card className="shadow-md">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ImageIcon className="h-4 w-4" />
            Loghi per Modulistica
          </CardTitle>
          <CardDescription>
            Carica i loghi che verranno utilizzati nei documenti della Modulistica.
            Seleziona il contesto branding (Studio o Agenzia) quando compili i moduli.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Logo Studio */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-teal-600" />
                Logo Gestione Studio
              </Label>
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 flex flex-col items-center justify-center min-h-[120px] bg-slate-50">
                {logoStudioUrl ? (
                  <div key="logo-studio-image" className="relative">
                    <img 
                      src={logoStudioUrl} 
                      alt="Logo Studio" 
                      className="max-h-24 max-w-full object-contain"
                    />
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                      onClick={() => handleLogoDelete('studio')}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div key="logo-studio-placeholder" className="text-center">
                    <ImageIcon className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Nessun logo</p>
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={fileInputStudioRef}
                accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                className="hidden"
                onChange={(e) => handleLogoUpload('studio', e.target.files?.[0])}
                data-testid="logo-studio-file-input"
              />
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={uploadingLogo === 'studio'}
                onClick={() => fileInputStudioRef.current?.click()}
              >
                {uploadingLogo === 'studio' ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                {logoStudioUrl ? 'Sostituisci Logo' : 'Carica Logo'}
              </Button>
              <p className="text-xs text-slate-400 text-center">PNG o JPG, max 5 MB</p>
              {logoError?.type === 'studio' && (
                <p className="text-xs text-red-600 text-center flex items-center justify-center gap-1" data-testid="logo-studio-error">
                  <AlertCircle className="h-3 w-3" />
                  {logoError.message}
                </p>
              )}
            </div>

            {/* Logo Agenzia */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-orange-600" />
                Logo Gestione Agenzia
              </Label>
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 flex flex-col items-center justify-center min-h-[120px] bg-slate-50">
                {logoAgenziaUrl ? (
                  <div key="logo-agenzia-image" className="relative">
                    <img 
                      src={logoAgenziaUrl} 
                      alt="Logo Agenzia" 
                      className="max-h-24 max-w-full object-contain"
                    />
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                      onClick={() => handleLogoDelete('agenzia')}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div key="logo-agenzia-placeholder" className="text-center">
                    <ImageIcon className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Nessun logo</p>
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={fileInputAgenziaRef}
                accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                className="hidden"
                onChange={(e) => handleLogoUpload('agenzia', e.target.files?.[0])}
                data-testid="logo-agenzia-file-input"
              />
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={uploadingLogo === 'agenzia'}
                onClick={() => fileInputAgenziaRef.current?.click()}
              >
                {uploadingLogo === 'agenzia' ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                {logoAgenziaUrl ? 'Sostituisci Logo' : 'Carica Logo'}
              </Button>
              <p className="text-xs text-slate-400 text-center">PNG o JPG, max 5 MB</p>
              {logoError?.type === 'agenzia' && (
                <p className="text-xs text-red-600 text-center flex items-center justify-center gap-1" data-testid="logo-agenzia-error">
                  <AlertCircle className="h-3 w-3" />
                  {logoError.message}
                </p>
              )}
            </div>
          </div>

          <Separator />

          {/* Agenzia Branding Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="studioNameAgenzia">Nome Agenzia</Label>
              <Input
                id="studioNameAgenzia"
                value={settings.studioNameAgenzia || ''}
                onChange={(e) => handleChange("studioNameAgenzia", e.target.value)}
                placeholder="Es. Prisma Servizi"
                data-testid="settings-agenzia-name"
              />
            </div>
            <div>
              <Label htmlFor="subtitleAgenzia">Sottotitolo Agenzia</Label>
              <Input
                id="subtitleAgenzia"
                value={settings.subtitleAgenzia || ''}
                onChange={(e) => handleChange("subtitleAgenzia", e.target.value)}
                placeholder="Es. Agenzia per il Lavoro"
                data-testid="settings-agenzia-subtitle"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Address */}
      <Card className="shadow-md">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Sede Legale
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="address">Indirizzo</Label>
              <Input
                id="address"
                value={settings.address}
                onChange={(e) => handleChange("address", e.target.value)}
                placeholder="Es. Via Roma, 123"
                data-testid="settings-address"
              />
            </div>
            <div>
              <Label htmlFor="city">Città</Label>
              <Input
                id="city"
                value={settings.city}
                onChange={(e) => handleChange("city", e.target.value)}
                placeholder="Civitavecchia"
                data-testid="settings-city"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="province">Provincia</Label>
                <Input
                  id="province"
                  value={settings.province}
                  onChange={(e) => handleChange("province", e.target.value.toUpperCase().slice(0, 2))}
                  placeholder="RM"
                  maxLength={2}
                  data-testid="settings-province"
                />
              </div>
              <div>
                <Label htmlFor="postalCode">CAP</Label>
                <Input
                  id="postalCode"
                  value={settings.postalCode}
                  onChange={(e) => handleChange("postalCode", e.target.value.slice(0, 5))}
                  placeholder="00053"
                  maxLength={5}
                  data-testid="settings-postal-code"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contacts - Studio */}
      <Card className="shadow-md">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-4 w-4 text-teal-600" />
            Contatti Gestione Studio
          </CardTitle>
          <CardDescription>
            Contatti utilizzati per comunicazioni, modulistica e fatture relative alla Gestione Studio
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="phoneStudio">Telefono / WhatsApp Studio</Label>
              <Input
                id="phoneStudio"
                value={settings.phoneStudio || settings.phone || ''}
                onChange={(e) => handleChange("phoneStudio", e.target.value)}
                placeholder="333 123 4567"
                data-testid="settings-phone-studio"
              />
            </div>
            <div>
              <Label htmlFor="emailStudio">Email Studio</Label>
              <Input
                id="emailStudio"
                type="email"
                value={settings.emailStudio || settings.email || ''}
                onChange={(e) => handleChange("emailStudio", e.target.value)}
                placeholder="Es. info@studio.it"
                data-testid="settings-email-studio"
              />
            </div>
            <div>
              <Label htmlFor="pecStudio">PEC Studio</Label>
              <Input
                id="pecStudio"
                type="email"
                value={settings.pecStudio || settings.pec || ''}
                onChange={(e) => handleChange("pecStudio", e.target.value)}
                placeholder="Es. studio@pec.it"
                data-testid="settings-pec-studio"
              />
            </div>
            <div>
              <Label htmlFor="sdiCodeStudio">Codice SDI Studio</Label>
              <Input
                id="sdiCodeStudio"
                value={settings.sdiCodeStudio || ''}
                onChange={(e) => handleChange("sdiCodeStudio", e.target.value.toUpperCase())}
                placeholder="Es. SU9YNJA"
                data-testid="settings-sdi-studio"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contacts - Agenzia */}
      <Card className="shadow-md">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-orange-600" />
            Contatti Gestione Agenzia
          </CardTitle>
          <CardDescription>
            Contatti utilizzati per comunicazioni, modulistica e fatture relative alla Gestione Agenzia
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="phoneAgenzia">Telefono / WhatsApp Agenzia</Label>
              <Input
                id="phoneAgenzia"
                value={settings.phoneAgenzia || ''}
                onChange={(e) => handleChange("phoneAgenzia", e.target.value)}
                placeholder="333 456 7890"
                data-testid="settings-phone-agenzia"
              />
            </div>
            <div>
              <Label htmlFor="emailAgenzia">Email Agenzia</Label>
              <Input
                id="emailAgenzia"
                type="email"
                value={settings.emailAgenzia || ''}
                onChange={(e) => handleChange("emailAgenzia", e.target.value)}
                placeholder="Es. info@agenzia.it"
                data-testid="settings-email-agenzia"
              />
            </div>
            <div>
              <Label htmlFor="pecAgenzia">PEC Agenzia</Label>
              <Input
                id="pecAgenzia"
                type="email"
                value={settings.pecAgenzia || ''}
                onChange={(e) => handleChange("pecAgenzia", e.target.value)}
                placeholder="Es. agenzia@pec.it"
                data-testid="settings-pec-agenzia"
              />
            </div>
            <div>
              <Label htmlFor="sdiCodeAgenzia">Codice SDI Agenzia</Label>
              <Input
                id="sdiCodeAgenzia"
                value={settings.sdiCodeAgenzia || ''}
                onChange={(e) => handleChange("sdiCodeAgenzia", e.target.value.toUpperCase())}
                placeholder="Es. SU9YNJA"
                data-testid="settings-sdi-agenzia"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bank Details */}
      <Card className="shadow-md">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Coordinate Bancarie
          </CardTitle>
          <CardDescription>
            Questi dati verranno mostrati nelle fatture con metodo di pagamento "Bonifico"
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="iban">IBAN</Label>
              <Input
                id="iban"
                value={settings.iban}
                onChange={(e) => handleChange("iban", e.target.value.toUpperCase())}
                placeholder="Es. IT60 X054 2811 1010 0000 0123 456"
                data-testid="settings-iban"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="bankName">Nome Banca</Label>
              <Input
                id="bankName"
                value={settings.bankName}
                onChange={(e) => handleChange("bankName", e.target.value)}
                placeholder="Es. Banca Intesa S.p.A."
                data-testid="settings-bank-name"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Templates (multi) — v1.4.1 (17/07/2026).
          Sostituisce la vecchia sezione a template singolo. La sezione
          resta indipendente dal save globale delle impostazioni: ogni
          template si salva/elimina via API dedicata. */}
      <EmailTemplatesManager />

      {/* Default office invoice presets (payment method + VAT + management type).
          Resolves shared studio_settings.default* fields. */}
      <Card className="shadow-md border-amber-200" data-testid="settings-default-payment-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-amber-600" />
            Preimpostazioni Nuova Fattura
          </CardTitle>
          <CardDescription>
            Valori preselezionati quando si crea una nuova fattura (sia
            manuale che da pratica). Le fatture già emesse non vengono
            modificate; tutti i campi restano editabili in fase di
            emissione. "Tipo Gestione" per fatture create da pratica è
            comunque derivato dal tipo pratica (business rule).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-md">
            <Label htmlFor="defaultInvoicePaymentMethod">Metodo Pagamento Predefinito</Label>
            <Select
              value={settings.defaultInvoicePaymentMethod || "Bonifico"}
              onValueChange={(value) =>
                handleChange("defaultInvoicePaymentMethod", value)
              }
            >
              <SelectTrigger
                id="defaultInvoicePaymentMethod"
                data-testid="settings-default-payment-method-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Bonifico">Bonifico bancario vista fattura</SelectItem>
                <SelectItem value="POS">Pagamento effettuato con POS con quietanza in fattura</SelectItem>
                <SelectItem value="Contanti">Pagamento effettuato in contanti con quietanza</SelectItem>
                <SelectItem value="ContantiVistaFattura">Contanti vista fattura</SelectItem>
                <SelectItem value="Assegno">Assegno bancario</SelectItem>
                <SelectItem value="RimessaDiretta">Rimessa diretta / presentazione fattura</SelectItem>
                <SelectItem value="Altro">Altro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="defaultInvoiceVatRate">Aliquota IVA Predefinita (%)</Label>
              <Input
                id="defaultInvoiceVatRate"
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={
                  settings.defaultInvoiceVatRate === undefined ||
                  settings.defaultInvoiceVatRate === null
                    ? 22
                    : settings.defaultInvoiceVatRate
                }
                onChange={(e) => {
                  const raw = e.target.value;
                  // Allow blank during typing (treated as "unset" on save).
                  if (raw === "") {
                    handleChange("defaultInvoiceVatRate", "");
                    return;
                  }
                  const num = Number(raw);
                  if (!Number.isNaN(num)) {
                    handleChange("defaultInvoiceVatRate", num);
                  }
                }}
                data-testid="settings-default-vat-rate-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Es. 22 = 22% IVA. Vuoto = fallback 22%.
              </p>
            </div>

            <div>
              <Label htmlFor="defaultInvoiceManagementType">Tipo Gestione Predefinito</Label>
              <Select
                value={settings.defaultInvoiceManagementType || "agenzia"}
                onValueChange={(value) =>
                  handleChange("defaultInvoiceManagementType", value)
                }
              >
                <SelectTrigger
                  id="defaultInvoiceManagementType"
                  data-testid="settings-default-management-type-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agenzia">Gestione Agenzia</SelectItem>
                  <SelectItem value="studio">Gestione Studio</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Applicato solo alle fatture create manualmente.
              </p>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label
              htmlFor="defaultInvoiceImportoLordo"
              className="flex items-start gap-3 cursor-pointer rounded-md border border-slate-200 p-3 hover:bg-slate-50 transition-colors"
            >
              <Checkbox
                id="defaultInvoiceImportoLordo"
                checked={settings.defaultInvoiceImportoLordo !== false}
                onCheckedChange={(checked) =>
                  handleChange("defaultInvoiceImportoLordo", checked === true)
                }
                data-testid="settings-default-importo-lordo-checkbox"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-800">
                  Importo Lordo (IVA inclusa) per default
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Spuntato = il campo "Importo" è interpretato come lordo
                  e scorporato automaticamente. Non spuntato = importo netto.
                  Per fatture da pratica resta dominante la regola cliente
                  (Privato / no P.IVA → Lordo).
                </p>
              </div>
            </label>

            <label
              htmlFor="defaultInvoiceContributoEnabled"
              className="flex items-start gap-3 cursor-pointer rounded-md border border-slate-200 p-3 hover:bg-slate-50 transition-colors"
            >
              <Checkbox
                id="defaultInvoiceContributoEnabled"
                checked={settings.defaultInvoiceContributoEnabled !== false}
                onCheckedChange={(checked) =>
                  handleChange("defaultInvoiceContributoEnabled", checked === true)
                }
                data-testid="settings-default-contributo-enabled-checkbox"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-800">
                  Contributo Integrativo 4% attivo per default
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Spuntato = il 4% Cassa è calcolato automaticamente sulle
                  nuove fatture. Si può sempre disattivare nel singolo
                  documento.
                </p>
              </div>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Card className="shadow-md">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            {/* Status messages - using hidden class for simplicity */}
            <div className="flex items-center gap-2 min-h-[28px]">
              <div 
                className={`flex items-center gap-2 text-green-600 ${
                  saveStatus === 'success' ? '' : 'hidden'
                }`}
              >
                <CheckCircle2 className="h-5 w-5" />
                <span>Impostazioni salvate con successo</span>
              </div>
              <div 
                className={`flex items-center gap-2 text-red-600 ${
                  saveStatus === 'error' ? '' : 'hidden'
                }`}
              >
                <AlertCircle className="h-5 w-5" />
                <span>Errore durante il salvataggio</span>
              </div>
              <span 
                className={`text-amber-600 text-sm ${
                  !saveStatus && hasChanges ? '' : 'hidden'
                }`}
              >
                Modifiche non salvate
              </span>
            </div>
            <Button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-settings-button"
            >
              <Save className={`h-4 w-4 mr-2 ${isSaving ? 'hidden' : ''}`} />
              <Loader2 className={`h-4 w-4 mr-2 animate-spin ${isSaving ? '' : 'hidden'}`} />
              Salva Impostazioni
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
